// Logika "device-aware" detail kapal yg dipakai bareng sidebar (ShipSection.vue, render Vue)
// & popup peta (mapService.js, render HTML string) biar perilaku + datanya konsisten, ga drift.

import { getRpmTypeLabel } from '@/util/fms-summary';
import { kmhToKnot } from '@/util/units';
import LandmarkSvc from '@/services/LandmarkSvc';
import { convertDecimalToDMS } from '@/views/map/utils/coordinates';

// Label status kapal.
export const STATUS_LABELS = {
  online: 'Online',
  offline: 'Offline',
  'no-gps': 'No GPS',
  'no-sensor': 'No Sensor',
};

// Warna status buat badge. Sidebar pakai CSS class, popup pakai inline style — sumbernya sini.
export const STATUS_COLORS = {
  online: '#28a745',
  offline: '#ff3b30',
  'no-gps': '#9ca3af',
  'no-sensor': '#f97316',
};

// Pilihan rentang Vessel Trail (hari).
export const TRAIL_DAY_OPTIONS = [1, 3, 7, 14];

export function resolveStatus(ship) {
  if (ship.shipStatus) return ship.shipStatus;
  return ship.dataStatus ? 'online' : 'offline';
}

export function statusLabel(ship) {
  return STATUS_LABELS[resolveStatus(ship)] || 'Offline';
}

export function statusColor(ship) {
  return STATUS_COLORS[resolveStatus(ship)] || STATUS_COLORS.offline;
}

// userAccess = state.auth.userAccess ({ module: ['read', ...] }). Kalau gak dioper
// (undefined), gak ada pembatasan - biar caller lama yang belum di-update tetep jalan
// apa adanya.
export function canReadModule(userAccess, moduleKey) {
  if (!userAccess) return true;
  return !!userAccess[moduleKey]?.includes('read');
}

export function hasNum(v) {
  return v !== null && v !== undefined && !Number.isNaN(Number(v));
}

export function prettyLabel(raw) {
  // PORT_IN -> "Port In", STARBOARD_OUT -> "Stbd Out", AE_INLINE_IN -> "AE Inline In",
  // BUNKER_IN -> "Bunker In", AE4_IN -> "AE4 In", CENTER_IN -> "Center In".
  if (!raw) return '';
  return raw
    .split('_')
    .map((word) => {
      if (word === 'AE' || /^AE\d+$/.test(word)) return word;
      if (word === 'IN') return 'In';
      if (word === 'OUT') return 'Out';
      if (word === 'STARBOARD') return 'Stbd';
      return word.charAt(0) + word.slice(1).toLowerCase();
    })
    .join(' ');
}

export function fmLabel(fm) {
  const raw = (fm.device && fm.device.devcLabel) || fm.deviceId || '';
  return prettyLabel(raw);
}

export function tempLabel(item) {
  const pos = { PORT: 'Port', STARBOARD: 'Stbd', CENTER: 'Center' }[item.type] || (item.type || '');
  const kind = item.device && item.device.devcLabel === 'EXHAUST_TEMPERATURE' ? 'Exhaust' : 'Reservoir';
  return `${pos} ${kind}`.trim();
}

export function tempValue(item) {
  // -999 = sentinel sensor mati; null/undefined = belum ada baca.
  const t = item.temperature;
  if (t === null || t === undefined || t === -999) return '-';
  return `${Math.floor(t)} °C`;
}

export function emptyRow() {
  return { label: '—', value: 'No data' };
}

export function latestDatetime(dataArr) {
  // Ambil datetime dari data point dengan timestamp paling baru.
  if (!dataArr || !dataArr.length) return null;
  let best = null;
  let bestTs = -Infinity;
  dataArr.forEach((d) => {
    const ts = d.timestamp || 0;
    if (ts > bestTs) { bestTs = ts; best = d; }
  });
  return best ? best.datetime : null;
}

export function formatDataTime(dt) {
  // "2026-06-10 17:49:56" -> { date, time }
  if (!dt || typeof dt !== 'string') return null;
  const [date, time] = dt.split(' ');
  return { date: date || '', time: (time || '').slice(0, 8) };
}

export function speedKn(ship) {
  const speed = ship.lastGpsData && ship.lastGpsData.speed;
  if (speed === null || speed === undefined || Number.isNaN(Number(speed))) return null;
  return (Number(speed) / 1.852).toFixed(2);
}

export function rpmList(ship, userAccess) {
  if (!canReadModule(userAccess, 'series_device_rpm')) return [];
  const types = [...new Set((ship.lastRpmData || []).map((rpm) => rpm.type))];
  return types.map((type) => {
    const data = (ship.lastRpmData || []).find((rpm) => rpm.type === type);
    return {
      label: getRpmTypeLabel(type, types),
      value: Math.floor((data && data.rpm) || 0),
    };
  });
}

export function locationDescription(ship) {
  const info = ship.locationInfo;
  if (!info) return null;
  switch (info.status) {
    case 'landmark':
      return info.landmark?.lndmName || info.name || null;
    case 'sea':
    case 'inland':
      return info.name || null;
    case 'land':
      return 'On land';
    default:
      return info.name || null;
  }
}

export async function describeLocation(latitude, longitude) {
  if (latitude === undefined || latitude === null || longitude === undefined || longitude === null) {
    return null;
  }
  try {
    const res = await LandmarkSvc.landmarks.locate({ latitude, longitude });
    return locationDescription({ locationInfo: res?.data?.data || null });
  } catch (e) {
    console.warn('Failed to reverse-geocode point:', e);
    return null;
  }
}

export function totalCruiseNm(ship) {
  const distance = ship.summary && ship.summary.gps
    && ship.summary.gps.summary && ship.summary.gps.summary.totalDistance;
  return distance ? (distance / 1852).toFixed(2) : null;
}

export function fmtHours(sec) {
  const h = Math.floor(sec / 3600);
  const m = Math.round((sec % 3600) / 60);
  if (h && m) return `${h}h ${m}m`;
  if (h) return `${h}h`;
  return `${m}m`;
}

export function hasRpmDevice(ship) {
  // Aset memang punya sensor RPM? Pakai flag deviceStatus, fallback ke device list.
  if (ship.deviceStatus && typeof ship.deviceStatus.hasRpm === 'boolean') {
    return ship.deviceStatus.hasRpm;
  }
  return (ship.devices || []).some((d) => d.devcType === 'rpm');
}

// Satu grup per tipe device yang dimiliki aset; tiap grup jadi 1 halaman pager.
// Tiap grup: { key, label, rows?:[{label,value,full?}], cards?:[{title,time,rows}], time? }.
export function buildDetailGroups(ship, userAccess) {
  const ds = ship.deviceStatus || {};
  const groups = [];

  // Position (GPS) + RPM merged — tiap section bawa timestampnya sendiri di atas rows-nya.
  const gps = ship.lastGpsData || {};
  const hasRpmData = canReadModule(userAccess, 'series_device_rpm')
    && (ds.hasRpm || (ship.lastRpmData || []).length > 0);
  const speed = speedKn(ship);
  // Report bikin ship-nya sendiri dan cuma ngisi lastLatLng, bukan lastCoordinate.
  const latLng = ship.lastLatLng;
  const coordinate = ship.lastCoordinate
    || (hasNum(latLng?.lat) && hasNum(latLng?.lng)
      ? `${convertDecimalToDMS(latLng.lat, true)}, ${convertDecimalToDMS(latLng.lng, false)}`
      : '-');
  const gpsRows = [{ label: 'Coordinates', value: coordinate, full: true }];
  const location = locationDescription(ship);
  if (location) gpsRows.push({ label: 'Location', value: location, full: true });
  gpsRows.push({ label: 'Speed', value: speed !== null ? `${speed} kn` : '-' });
  gpsRows.push({ label: 'Heading', value: hasNum(gps.track) ? `${Math.round(gps.track)}°` : '-' });
  const sections = [{ time: gps.datetime || null, rows: gpsRows }];
  if (hasRpmData) {
    const rpmRows = rpmList(ship, userAccess).map((r) => ({ label: `${r.label} RPM`, value: String(r.value) }));
    sections.push({ time: latestDatetime(ship.lastRpmData), rows: rpmRows.length ? rpmRows : [emptyRow()] });
  }
  groups.push({
    key: 'gps',
    label: hasRpmData ? 'Position & RPM' : 'Position',
    sections,
  });

  // Auxiliary engine — field "rpm" di pulse AE sebenarnya running seconds, jadi tampilkan ON/OFF aja.
  if (canReadModule(userAccess, 'series_device_ae') && (ds.hasAe || (ship.lastAeData || []).length)) {
    const rows = [...(ship.lastAeData || [])]
      .sort((a, b) => (a.no || 0) - (b.no || 0))
      .map((ae) => ({
        label: `AE ${(ae.no || 0) + 1}`,
        value: (ae.rpm || 0) > 0 ? 'ON' : 'OFF',
      }));
    groups.push({
      key: 'ae', label: 'Auxiliary', rows: rows.length ? rows : [emptyRow()], time: latestDatetime(ship.lastAeData),
    });
  }

  // Flowmeter (fuel) — tiap flowmeter jadi card kecil (Flow/Temp/Density), bawa jam datanya sendiri.
  if (canReadModule(userAccess, 'series_device_flowmeter') && (ds.hasFlowmeter || (ship.lastFmData || []).length)) {
    const cards = [...(ship.lastFmData || [])]
      .sort((a, b) => fmLabel(a).localeCompare(fmLabel(b)))
      .map((fm) => {
        const temp = fm.temperature;
        return {
          title: fmLabel(fm),
          time: formatDataTime(fm.datetime),
          rows: [
            { label: 'Flow', value: `${Math.floor(fm.volumeFlowrate || 0)} L/h` },
            { label: 'Temp', value: hasNum(temp) && temp !== -999 ? `${Math.floor(temp)} °C` : '-' },
            { label: 'Density', value: hasNum(fm.density) ? Number(fm.density).toFixed(3) : '-' },
          ],
        };
      });
    if (cards.length) groups.push({ key: 'flowmeter', label: 'Fuel', cards });
    else groups.push({ key: 'flowmeter', label: 'Fuel', rows: [emptyRow()] });
  }

  // Temperature — exhaust & reservoir per posisi, -999 dirender '-'.
  if (canReadModule(userAccess, 'series_device_temperature') && (ds.hasTemperature || (ship.lastTemperatureData || []).length)) {
    const rows = (ship.lastTemperatureData || []).map((item) => ({
      label: tempLabel(item),
      value: tempValue(item),
    }));
    groups.push({
      key: 'temperature', label: 'Temp', rows: rows.length ? rows : [emptyRow()], time: latestDatetime(ship.lastTemperatureData),
    });
  }

  return groups;
}

// Ringkasan window trail — diturunin dari summary endpoint yg sama yg gambar polyline.
export function buildTrailStats(ship) {
  const s = ship.summary;
  if (!s) return [];
  const gps = (s.gps && s.gps.summary) || {};
  const fm = (s.flowmeter && s.flowmeter.summary) || {};
  const rpmAvg = (s.rpm && s.rpm.summary && s.rpm.summary.average) || {};
  const rows = [];

  // Total Cruise
  const cruise = totalCruiseNm(ship);
  if (cruise !== null) rows.push({ label: 'Total Cruise', value: `${cruise} NM` });

  // Window kosong (ga ada data di rentang ini) balikin semua 0 — skip biar ga nampilin 0 palsu.
  const hasGpsWindow = hasNum(gps.totalDuration) && Number(gps.totalDuration) > 0;

  // Avg Speed (knots)
  if (hasGpsWindow && hasNum(gps.averageSpeed)) {
    rows.push({ label: 'Avg Speed', value: `${kmhToKnot(gps.averageSpeed).toFixed(1)} kn` });
  }

  // Moving Time + % underway
  if (hasGpsWindow && hasNum(gps.movingTime)) {
    const pct = Math.round((gps.movingTime / gps.totalDuration) * 100);
    rows.push({ label: 'Moving Time', value: `${fmtHours(gps.movingTime)} · ${pct}%` });
  }

  // Fuel ME
  if (hasNum(fm.meEngineCons) && Number(fm.meEngineCons) > 0) {
    const liters = Math.round(Number(fm.meEngineCons));
    rows.push({ label: 'Fuel ME', value: `${liters.toLocaleString('id-ID')} L` });
  }

  // Avg RPM per tipe mesin (P/C/S)
  const order = { PORT: 0, CENTER: 1, STARBOARD: 2 };
  const short = { PORT: 'P', CENTER: 'C', STARBOARD: 'S' };
  const rank = (k) => (order[k] === undefined ? 9 : order[k]);
  const rpmKeys = Object.keys(rpmAvg)
    .filter((k) => hasNum(rpmAvg[k]) && Number(rpmAvg[k]) > 0)
    .sort((a, b) => rank(a) - rank(b));
  if (rpmKeys.length) {
    const txt = rpmKeys.length === 1
      ? String(Math.round(rpmAvg[rpmKeys[0]]))
      : rpmKeys.map((k) => `${short[k] || k} ${Math.round(rpmAvg[k])}`).join(' / ');
    rows.push({ label: 'Avg RPM', value: txt });
  }

  // Sea State (tinggi ombak · kondisi · periode)
  if (hasGpsWindow && hasNum(gps.averageWaveHeight)) {
    const parts = [`${Number(gps.averageWaveHeight).toFixed(1)} m`];
    if (gps.averageWaveHeightString) parts.push(prettyLabel(gps.averageWaveHeightString));
    if (hasNum(gps.averageWavePeriod) && Number(gps.averageWavePeriod) > 0) {
      parts.push(`${Number(gps.averageWavePeriod).toFixed(0)}s`);
    }
    rows.push({ label: 'Sea State', value: parts.join(' · ') });
  }

  return rows;
}
